import { concat, fn, hash } from '@ember/helper';
import { action } from '@ember/object';
import { LinkTo } from '@ember/routing';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismCard,
  PrismMessage,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { and, not } from 'ember-truth-helpers';
import { RI_BREAKDOWNS } from 'frontend-utils/utils/constants/constant-values';
import HotelAndMarketRevenueInsightBaseSegmentationBlockBase, {
  type HotelAndMarketRevenueInsightBaseSegmentationBlockBaseArgs,
  type HotelAndMarketRevenueInsightBaseSegmentationBlockBaseSignature,
} from 'lumen/components/hotel-and-market/revenue-insight/base/segmentation-block-base';
import type { SegmentationItem } from 'lumen/components/hotel-and-market/revenue-insight/base/segmentation-block-item-base';
import HotelAndMarketRevenueInsightBlockList from 'lumen/components/hotel-and-market/revenue-insight/block-list';
import HotelAndMarketRevenueInsightSegmentationSegmentationBlockTitle from 'lumen/components/hotel-and-market/revenue-insight/segmentation/segmentation-block-title';
import HotelAndMarketRevenueInsightSegmentationSegmentationItem from 'lumen/components/hotel-and-market/revenue-insight/segmentation/segmentation-item';
import hasFeature from 'lumen/helpers/has-feature';
import highlightElement from 'lumen/modifiers/highlight-element';
import { BREAKDOWNS } from 'lumen/utils/central-mapping-constants';
import { NB_OF_SEGMENTATION_VALUES_TO_DISPLAY } from 'lumen/utils/constants/ri-constant-values';
import { segmentationIdFormatter } from 'lumen/utils/revenue-insight/segmentation-value-formatter';

import styles from './segmentation-block.module.css';

interface HotelAndMarketRevenueInsightSegmentationBaseSegmentationBlockArgs extends HotelAndMarketRevenueInsightBaseSegmentationBlockBaseArgs {
  cardContentClassName: string;
  extra: { hasHotelsThatMapByRateCode: boolean };
}

interface HotelAndMarketRevenueInsightSegmentationSegmentationBlockSignature extends HotelAndMarketRevenueInsightBaseSegmentationBlockBaseSignature {
  Args: HotelAndMarketRevenueInsightSegmentationBaseSegmentationBlockArgs;
  Element: HTMLDivElement;
}

export default class HotelAndMarketRevenueInsightSegmentationSegmentationBlock extends HotelAndMarketRevenueInsightBaseSegmentationBlockBase<HotelAndMarketRevenueInsightSegmentationSegmentationBlockSignature> {
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked expandedIds: string[] = [];
  @tracked hasItemsAboveViewport = false;
  @tracked hasItemsBelowViewport = false;
  @tracked isNavigateToMappingLoading = false;
  @tracked isShowingAllSegmentationItems = false;

  updatedExpandedParents = (parentSegmentationValueId: string): void => {
    const { expandedIds } = this;
    const index = expandedIds.indexOf(parentSegmentationValueId);
    if (index === -1) {
      expandedIds.push(parentSegmentationValueId);
    } else {
      expandedIds.splice(index, 1);
    }
    this.expandedIds = expandedIds;
  };

  get breakdownDetailRoute(): string {
    return `${this.riSelections.currentRoute}.breakdown-detail`;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get contentParams() {
    const params = {
      ...super.contentParams,
      kpi: this.args.kpi,
      parsedSegmentationFilters: this.args.parsedSegmentationFilters,
      isDisabled: this.isDisabled,
      expandedIds: this.expandedIds,
    };

    return params;
  }

  get hasMoreItemsToShow(): boolean {
    const numberOfItems = this.sortedSegmentationObjects.length;
    return (
      !this.isSearchActive &&
      numberOfItems > NB_OF_SEGMENTATION_VALUES_TO_DISPLAY &&
      !this.isShowingAllSegmentationItems &&
      !this.args.isLoading
    );
  }

  get isDisabled(): boolean {
    const isCancellationLeadTime =
      this.segmentationName === RI_BREAKDOWNS.CANCELLATION_LEAD_TIME;
    if (isCancellationLeadTime) {
      return true;
    }
    const isBreakdownFilterLimitReached =
      this.args.filterLimits[this.segmentationName] === 0;
    return isBreakdownFilterLimitReached;
  }

  get segmentationBlockTitle(): string {
    const key = segmentationIdFormatter(
      this.segmentationName,
      this.riSelections,
    );
    return this.intl.t(key);
  }

  get showBreakdownDetail(): boolean {
    return !(
      this.riSelections.isCancellationsPercentageView &&
      this.segmentationName === RI_BREAKDOWNS.CANCELLATION_LEAD_TIME
    );
  }

  get showEmptyState(): boolean {
    if (this.args.isLoading) {
      return false;
    }
    if (this.segmentationName !== RI_BREAKDOWNS.SEGMENT) {
      return false;
    }
    const [firstItem] = this.sortedSegmentationObjects;
    return (
      !this.sortedSegmentationObjects.length ||
      (this.sortedSegmentationObjects.length === 1 &&
        firstItem?.segmentationValue === 'revenue_insight.left_to_map')
    );
  }

  get showExpandAndSearch(): boolean {
    return !this.showEmptyState;
  }

  get showMoreButtonLabel(): string {
    const noOfMoreItems =
      this.sortedSegmentationObjects.length -
      (NB_OF_SEGMENTATION_VALUES_TO_DISPLAY - 1);
    return this.intl.t('revenue_insight.breakdown_settings.action.show_more', {
      count: noOfMoreItems,
    });
  }

  get visibleSegmentations(): SegmentationItem[] {
    const visibleSegmentations: SegmentationItem[] = [];
    if (this.args.isLoading) {
      return visibleSegmentations;
    }
    const numberOfItems = this.sortedSegmentationObjects.length;
    const maxIndex = this.hasMoreItemsToShow
      ? NB_OF_SEGMENTATION_VALUES_TO_DISPLAY - 1
      : numberOfItems;
    for (let index = 0; index < maxIndex; index++) {
      const item = this.sortedSegmentationObjects[index]!;

      if (this.isSearchActive) {
        this.findMatchingItems(item, visibleSegmentations);
      } else {
        this.expandItems(item, visibleSegmentations);
      }
    }
    return visibleSegmentations;
  }

  expandItems(item: SegmentationItem, collection: SegmentationItem[]): void {
    collection.push(item);
    const isExpanded = this.expandedIds.includes(item.segmentationValueId);
    if (isExpanded) {
      if (item.children) {
        item.children.forEach((child) => this.expandItems(child, collection));
      }
    } else if (this.currentSelectedFilters.length && item.children) {
      collection.push(...[...this.expandSelectedItems(item)]);
    }
  }

  expandSelectedItems(item: SegmentationItem): Set<SegmentationItem> {
    let items = new Set<SegmentationItem>([]);
    let isFoundInChildren = false;
    item.children.forEach((child) => {
      if (this.currentSelectedFilters.includes(child.segmentationValueId)) {
        items.add(child);
        isFoundInChildren = true;
      } else if (isFoundInChildren) {
        items.add(child);
      }
      if (child.children) {
        const foundChildren = this.expandSelectedItems(child);
        if (foundChildren.size) {
          items.add(child);
          isFoundInChildren = true;
          items = new Set([...items, ...foundChildren]);
        }
      }
    });
    if (isFoundInChildren) {
      if (!this.expandedIds.includes(item.segmentationValueId)) {
        this.expandedIds.push(item.segmentationValueId);
      }
    }
    return items;
  }

  @action
  goToMappingSettings(): void {
    this.isNavigateToMappingLoading = true;
    const route = this.riSelections.isMultiProperty ? 'market' : 'hotel';
    this.router.transitionTo(`${route}.settings.central-mapping.mapping`, {
      queryParams: { breakdown: BREAKDOWNS.MARKET_SEGMENT },
    });
    this.usageAnalytics.track('Unmapped Data Opened', {
      eventTrigger: 'empty-state',
    });
  }

  @action
  handleListScroll({
    hasItemsBelowViewport,
    hasItemsAboveViewport,
  }: {
    hasItemsAboveViewport: boolean;
    hasItemsBelowViewport: boolean;
  }): void {
    this.hasItemsAboveViewport = hasItemsAboveViewport;
    this.hasItemsBelowViewport = hasItemsBelowViewport;
  }

  @action
  onExpand(): void {
    this.usageAnalytics.track('Detailed View Opened', {
      eventTrigger: 'detailed-view-button',
      viewName: this.segmentationName,
    });
  }

  @action
  onShowMore(): void {
    this.isShowingAllSegmentationItems = true;
    this.hasItemsBelowViewport = true;
  }

  @action
  toggleSearch(isEnabled: boolean): void {
    this.updateIsSearchActive(isEnabled);
  }

  <template>
    {{#if this.showEmptyState}}
      <PrismCard @contentLayout="noPadding" class={{styles.card}} ...attributes>
        <:content>
          <div class="{{styles.card-content}} h-full {{@cardContentClassName}}">
            <div class="flex h-full items-center justify-center">
              <PrismMessage
                @description={{t
                  "revenue_insight.breakdowns.segmentation_empty_state_body"
                }}
                @illustration="100x100_market-insight-leadtime"
                @title={{t
                  "revenue_insight.breakdowns.segmentation_empty_state_heading"
                }}
              >
                <:link>
                  <p class="prism-link text-300--regular">
                    {{#if (hasFeature "revenueinsight.pms.edit")}}
                      <PrismButton
                        @iconAfter="arrow-right"
                        @isDisabled={{this.isNavigateToMappingLoading}}
                        @isLoading={{this.isNavigateToMappingLoading}}
                        @label={{t
                          "revenue_insight.breakdown_settings.action.map_data"
                        }}
                        @onClick={{this.goToMappingSettings}}
                      />
                    {{else}}
                      <span>{{t
                          "revenue_insight.breakdowns.segmenation_empty_state_link.viewer"
                        }}</span>
                    {{/if}}
                  </p>
                </:link>
              </PrismMessage>
            </div>
          </div>
        </:content>
      </PrismCard>
    {{else}}
      <PrismCard
        @contentLayout="noPadding"
        class="{{styles.card}}
          {{if this.isSearchActive styles.search-active}}
          {{if this.hasMoreItemsToShow styles.with-has-more}}
          {{if this.isShowingAllSegmentationItems styles.with-showing-all}}
          {{if this.hasItemsAboveViewport styles.with-top-overlay}}
          {{if this.hasItemsBelowViewport styles.with-bottom-overlay}}"
        {{highlightElement
          id=(concat "revenue-insight-kpi-block-" this.segmentationName)
          isLoading=@isLoading
        }}
        ...attributes
      >
        <:header as |Header|>
          <HotelAndMarketRevenueInsightSegmentationSegmentationBlockTitle
            @extra={{@extra}}
            @isDisabled={{this.isDisabled}}
            @params={{this.titleParams}}
          >
            <Header.title>
              {{this.segmentationBlockTitle}}
            </Header.title>
          </HotelAndMarketRevenueInsightSegmentationSegmentationBlockTitle>
        </:header>

        <:headerAfter>
          <div class="flex items-center gap-50">
            {{#if this.isSearchActive}}
              <PrismButton
                @buttonType="ghost"
                @icon="cross"
                @onClick={{fn this.toggleSearch false}}
              />
            {{else if (and (not @isLoading) this.showExpandAndSearch)}}
              <PrismButton
                @buttonType="ghost"
                @icon="search"
                @onClick={{fn this.toggleSearch true}}
                data-event-id="hotel-revenue-insight-breakdown-search"
                data-event-value={{this.segmentationName}}
              />

              {{#if this.showBreakdownDetail}}
                <LinkTo
                  @query={{hash segmentationId=this.segmentationName}}
                  @route={{this.breakdownDetailRoute}}
                  tabindex="-1"
                >
                  <PrismButton
                    @buttonType="ghost"
                    @icon="expand"
                    @onClick={{this.onExpand}}
                    data-event-id="hotel-revenue-insight-breakdown-expand"
                    data-event-value={{this.segmentationName}}
                  />
                </LinkTo>
              {{/if}}
            {{/if}}
          </div>
        </:headerAfter>

        <:content>
          <div class="{{styles.card-content}} {{@cardContentClassName}}">
            <HotelAndMarketRevenueInsightBlockList
              @items={{this.visibleSegmentations}}
              {{! @glint-expect-error: Incorrect type }}
              @listItemComponent={{HotelAndMarketRevenueInsightSegmentationSegmentationItem}}
              @maxValues={{this.maxBlockValues}}
              @onListScroll={{this.handleListScroll}}
              @params={{this.contentParams}}
            />
          </div>

          {{#if this.hasMoreItemsToShow}}
            <div class="flex h-[48px] items-center justify-center py-200">
              <PrismButton
                @buttonType="secondary"
                @label={{this.showMoreButtonLabel}}
                @onClick={{this.onShowMore}}
                @size="small"
              />
            </div>
          {{/if}}
        </:content>
      </PrismCard>
    {{/if}}
  </template>
}
